import { CircularProgress, Stack, StandardTextFieldProps, TextField, Typography } from "@mui/material";
import { InputHTMLAttributes, ReactNode, useEffect, useRef } from "react";
import { Controller, ControllerProps, FieldValues } from "react-hook-form";
import { KbdShortcut } from "../ui/Kbd";

const KeyboardShortcuts = ({ isLoading }: { isLoading?: boolean }) => {
  if (isLoading) {
    return <CircularProgress size="2rem" />;
  }
  return (
    <Typography>
      Press <KbdShortcut>esc</KbdShortcut> to cancel; <KbdShortcut>enter</KbdShortcut> to save
    </Typography>
  );
};

const EditableTextField = <T extends FieldValues>({
  name,
  onKeyDown,
  control,
  rules,
  onBlur,
  inputRef,
  isLoading,
  onFocus,
  textFieldVariant,
  textFieldWidth,
  controls,
  seamless,
  splitTextCharacter,
  inputProps,
  type,
}: StandardTextFieldProps &
  Partial<ControllerProps<T>> & {
    isLoading: boolean;
    name: string;
    textFieldVariant?: "outlined" | "filled";
    textFieldWidth?: string;
    controls?: ReactNode;
    seamless?: boolean;
    splitTextCharacter?: string;
    type?: string;
    inputProps?: InputHTMLAttributes<HTMLInputElement>;
  }) => {
  const auxInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!seamless && auxInputRef.current) {
      auxInputRef.current?.focus();
      auxInputRef.current?.select();
    }
  }, [inputRef, seamless]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const text = splitTextCharacter ? field.value.split(splitTextCharacter)[0] : field.value;

        const textField = (
          <TextField
            {...field}
            value={text}
            type={type || "text"}
            sx={{ width: textFieldWidth || "30%", padding: 0 }}
            variant={textFieldVariant || "standard"}
            name={name}
            data-testid={name}
            aria-label={name}
            autoComplete="off"
            onKeyDown={onKeyDown}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error && fieldState.error.message}
            inputRef={inputRef || auxInputRef}
            onFocus={onFocus}
            onBlur={onBlur}
            size="large"
            className={seamless ? "seamless" : undefined}
            slotProps={{
              htmlInput: inputProps,
            }}
          />
        );

        return (
          <Stack direction="row" spacing={1} sx={{ width: "100%", alignItems: "center" }}>
            {seamless ? (
              <Stack sx={{ width: "min-content" }}>
                <Typography
                  aria-hidden="true"
                  variant="heading.six"
                  sx={{
                    visibility: "hidden",
                    height: "0px",
                    textWrap: "nowrap",
                  }}
                >
                  {text}
                </Typography>
                {textField}
              </Stack>
            ) : (
              textField
            )}
            {controls ? controls : <KeyboardShortcuts isLoading={isLoading} />}
          </Stack>
        );
      }}
    />
  );
};

export { EditableTextField };
